import { rufe } from './client';
import type { Beauftragungsbefund } from './honorare';

/**
 * Ressourcen, Belegungsübersicht und Zuordnung (Kap. 12).
 */

export type Ressourcenart =
  | 'modul'
  | 'raum'
  | 'fahrzeug'
  | 'betriebsmittel'
  | 'trainer'
  | 'dienstleister'
  | 'sonstiges';

/** Die Beschriftung je Art. Sie steht hier, damit sie überall gleich lautet. */
export const ARTNAME: Readonly<Record<Ressourcenart, string>> = {
  modul: 'Fahrmodul',
  raum: 'Raum',
  fahrzeug: 'Fahrzeug',
  betriebsmittel: 'Betriebsmittel',
  trainer: 'Trainer',
  dienstleister: 'Dienstleister',
  sonstiges: 'Sonstiges',
};

export const ARTEN: readonly Ressourcenart[] = [
  'modul',
  'raum',
  'fahrzeug',
  'betriebsmittel',
  'trainer',
  'dienstleister',
  'sonstiges',
];

export type Ressourcenzeile = {
  readonly id: string;
  readonly kennung: string;
  readonly bezeichnung: string;
  readonly art: Ressourcenart;
  readonly standort: string | null;
  readonly status: string;
  readonly belegungsart: 'ausschliesslich' | 'plaetze';
  readonly platzzahl: number | null;
  /**
   * ÄA-21: `intern`, `extern` — oder `null` bei allem, was keine Person ist.
   *
   * Der Auftraggeber am 11.09.2026: „es sind beides trainer, intern und
   * extern." Daran hängen zwei Regeln: die Auftragsverarbeitung (RES-39) und
   * die nachgelagerte Besetzung (RES-49).
   */
  readonly zugehoerigkeit: 'intern' | 'extern' | null;
  /**
   * RES-39: Der Vertragsstand der Auftragsverarbeitungsvereinbarung.
   *
   * Er steht in der LISTE und nicht erst in der Maske dahinter: Ein externer
   * Trainer ohne Vereinbarung sieht das Tagesbild nicht (ÄA-20), und wer dafür
   * 46 Ressourcen einzeln öffnen müsste, fände es nie.
   */
  readonly auftragsverarbeitung: string;
  readonly verantwortlicheRolle: string | null;
  /** AeA-05: hinterlegte Bausteine — nur an Modul und Raum sinnvoll. */
  readonly bausteineHinterlegt: number;
  /** AeA-05: wie viele aktive Bausteine es ueberhaupt gibt. */
  readonly bausteineGesamt: number;
};

/**
 * Warum eine Ressource nicht frei ist — der GRUND und nicht nur ein Nein.
 *
 * „Der Trainer ist am 14. gesperrt — Fortbildung" ist eine Auskunft, „nicht
 * verfügbar" ist eine Sackgasse (ERR-01).
 */
export type Freiheit =
  | { readonly art: 'frei'; readonly restplaetze: number | null }
  | { readonly art: 'ausser_betrieb'; readonly status: string }
  | { readonly art: 'ausserhalb_regelzeit' }
  | { readonly art: 'gesperrt'; readonly grund: string; readonly sperrart: string }
  | { readonly art: 'belegt'; readonly belegungen: number }
  | { readonly art: 'zu_wenig_plaetze'; readonly frei: number; readonly verlangt: number }
  | { readonly art: 'unbekannt' };

export type Vorschlag = Ressourcenzeile & { readonly restplaetze: number | null };

export type Belegungszeile = {
  readonly id: string;
  readonly ressource: string;
  readonly ressourcenkennung: string;
  readonly ressourcenbezeichnung: string;
  readonly ressourcenart: Ressourcenart;
  readonly standort: string | null;
  readonly termin: string | null;
  readonly status: string;
  readonly hart: boolean;
  readonly plaetze: number | null;
  readonly von: string;
  readonly bis: string;
  readonly verfaelltAm: string | null;
  readonly bemerkung: string | null;
  readonly terminbeginn: string | null;
  readonly terminbezeichnung: string | null;
};

function frage(werte: Record<string, string | undefined>): string {
  const p = new URLSearchParams();
  for (const [schluessel, wert] of Object.entries(werte)) {
    if (wert !== undefined && wert !== '') p.set(schluessel, wert);
  }
  return p.toString();
}

/**
 * Die zwei Kreise des Menüs.
 *
 * Der Auftraggeber am 11.09.2026: „ressourcenmenü mit zwei punkten Assets und
 * Trainer". Beides sind Ressourcen im Sinne von RES-01 — ein gemeinsames Modell
 * für Personen, Räume, Geräte und Fahrzeuge —, aber zwei verschiedene
 * Arbeitsgegenstände: An einem Fahrmodul pflegt man Bausteine und Platzzahlen,
 * an einem Trainer Qualifikationen, Verträge und Honorare.
 *
 * DIE AUFTEILUNG IST VOLLSTÄNDIG. Jede Art aus `ARTEN` steht in genau einem
 * Kreis — sonst gäbe es Ressourcen, die in keiner der beiden Sichten auftauchen,
 * und das sieht man ihnen nicht an. `kreistreue.test.ts` hält das fest.
 */
export const KREISE = {
  assets: ['modul', 'raum', 'fahrzeug', 'betriebsmittel', 'sonstiges'],
  trainer: ['trainer', 'dienstleister'],
} as const satisfies Record<string, readonly Ressourcenart[]>;

export type Kreis = keyof typeof KREISE;

export const KREISNAME: Readonly<Record<Kreis, string>> = {
  assets: 'Assets',
  trainer: 'Trainer',
};

/**
 * Die Standorte zur Auswahl (RES-01).
 *
 * Die Konfiguration bringt sie mit — dort gibt es schon eine Ressource. Beim
 * ANLEGEN gibt es noch keine, und der Standort stand deshalb als freies
 * Textfeld da: Wer „Lüneburg" tippte, legte die Ressource an einem Standort an,
 * den es nicht gibt (die Kennung lautet LUENEBURG), und merkte es erst, wenn
 * sie in keiner Planung auftauchte.
 */
export async function standorte(): Promise<
  readonly { readonly kennung: string; readonly bezeichnung: string }[]
> {
  const antwort = await rufe<{
    readonly standorte: readonly { readonly kennung: string; readonly bezeichnung: string }[];
  }>('/ressourcen/standorte');
  return antwort.standorte;
}

export async function liste(filter: {
  /** Eine oder mehrere Arten; der Server nimmt sie als Komma-Liste. */
  arten?: readonly Ressourcenart[];
  standort?: string;
}): Promise<readonly Ressourcenzeile[]> {
  const antwort = await rufe<{ readonly ressourcen: readonly Ressourcenzeile[] }>(
    `/ressourcen?${frage({ art: filter.arten?.join(','), standort: filter.standort })}`,
  );
  return antwort.ressourcen;
}

/** Die Belegungsübersicht (RES-17). */
export async function kalender(filter: {
  von: string;
  bis: string;
  arten?: readonly Ressourcenart[];
  standort?: string;
}): Promise<readonly Belegungszeile[]> {
  const antwort = await rufe<{ readonly belegungen: readonly Belegungszeile[] }>(
    `/ressourcen/kalender?${frage({
      von: filter.von,
      bis: filter.bis,
      art: filter.arten?.join(','),
      standort: filter.standort,
    })}`,
  );
  return antwort.belegungen;
}

/** Die Vorschlagsliste zu einem Bedarf (RES-18). */
export async function vorschlaege(bedarf: {
  von: string;
  bis: string;
  art?: string;
  eigenschaftArt?: string;
  eigenschaft?: string;
  standort?: string;
  plaetze?: string;
}): Promise<readonly Vorschlag[]> {
  const antwort = await rufe<{ readonly vorschlaege: readonly Vorschlag[] }>(
    `/ressourcen/vorschlaege?${frage(bedarf)}`,
  );
  return antwort.vorschlaege;
}

export async function frei(
  ressource: string,
  von: string,
  bis: string,
  plaetze?: string,
): Promise<Freiheit> {
  return rufe<Freiheit>(`/ressourcen/${ressource}/frei?${frage({ von, bis, plaetze })}`);
}

export async function belegen(eingabe: {
  ressource: string;
  von: string;
  bis: string;
  termin?: string;
  status?: 'vorlaeufig' | 'bestaetigt' | 'zugesagt';
  verfaelltAm?: string;
  plaetze?: number;
  bemerkung?: string;
}): Promise<{ readonly id: string }> {
  return rufe('/ressourcen/belegen', { verfahren: 'POST', rumpf: eingabe });
}

export async function absagen(belegung: string): Promise<{ readonly aufgehoben: boolean }> {
  return rufe(`/ressourcen/belegung/${belegung}/absagen`, { verfahren: 'POST' });
}

export async function anlegen(eingabe: {
  kennung: string;
  bezeichnung: string;
  art: Ressourcenart;
  standort?: string;
  belegungsart?: 'ausschliesslich' | 'plaetze';
  platzzahl?: number;
  verantwortlicheRolle?: string;
  /** ÄA-21: Pflicht bei Trainer und Dienstleister — die Datenbank verlangt sie. */
  zugehoerigkeit?: 'intern' | 'extern';
}): Promise<{ readonly id: string }> {
  return rufe('/ressourcen', { verfahren: 'POST', rumpf: eingabe });
}

// ---------------------------------------------------------------------------
// Die Konfiguration einer Ressource (RES-01 bis RES-04, RES-14, RES-15)
// ---------------------------------------------------------------------------

/** RES-02, RES-54: die sechs Sorten aus dem Datenmodell. */
export type Eigenschaftsart =
  | 'zertifikat'
  | 'sprache'
  | 'ausstattung'
  | 'fahrzeugklasse'
  | 'qualifikation'
  | 'sonstiges';

export const EIGENSCHAFTSARTEN: readonly Eigenschaftsart[] = [
  'qualifikation',
  'zertifikat',
  'sprache',
  'ausstattung',
  'fahrzeugklasse',
  'sonstiges',
];

export const EIGENSCHAFTSARTNAME: Readonly<Record<Eigenschaftsart, string>> = {
  qualifikation: 'Qualifikation',
  zertifikat: 'Zertifikat',
  sprache: 'Sprache',
  ausstattung: 'Ausstattung',
  fahrzeugklasse: 'Fahrzeugklasse',
  sonstiges: 'Sonstiges',
};

/**
 * Eine Eigenschaft oder Qualifikation (RES-02, RES-54).
 *
 * Der SCHLÜSSEL ist das, wonach ein Bedarf sucht; die Bezeichnung ist die
 * Anzeige. Ohne diese Trennung machte die erste Umbenennung („Beamer" →
 * „Projektor") jeden Bedarf ungedeckt.
 */
export type Eigenschaftszeile = {
  readonly id: string;
  readonly art: string;
  readonly schluessel: string;
  readonly bezeichnung: string | null;
  readonly gueltigAb: string | null;
  readonly gueltigBis: string | null;
  readonly nachweis: string | null;
  /** RES-54: läuft in den nächsten 90 Tagen ab. */
  readonly laeuftBaldAb: boolean;
  /** Abgelaufen — deckt keinen Bedarf mehr (TAG-14). */
  readonly abgelaufen: boolean;
};

export const WOCHENTAGE: readonly { readonly nr: number; readonly name: string }[] = [
  { nr: 1, name: 'Mo' },
  { nr: 2, name: 'Di' },
  { nr: 3, name: 'Mi' },
  { nr: 4, name: 'Do' },
  { nr: 5, name: 'Fr' },
  { nr: 6, name: 'Sa' },
  { nr: 7, name: 'So' },
];

export type Sperrart = 'abwesenheit' | 'wartung' | 'anderweitig' | 'feiertag' | 'sonstiges';

export const SPERRARTEN: readonly Sperrart[] = [
  'wartung',
  'abwesenheit',
  'anderweitig',
  'feiertag',
  'sonstiges',
];

export const SPERRARTNAME: Readonly<Record<Sperrart, string>> = {
  wartung: 'Wartung',
  abwesenheit: 'Abwesenheit',
  anderweitig: 'anderweitig gebunden',
  feiertag: 'Feiertag',
  sonstiges: 'Sonstiges',
};

export type Sperrzeitzeile = {
  readonly id: string;
  readonly art: string;
  readonly grund: string;
  readonly von: string;
  readonly bis: string;
  readonly wiederholungTage: number | null;
  readonly vorbei: boolean;
};

export const STATUS: readonly string[] = ['aktiv', 'wartung', 'ausser_betrieb', 'ausgemustert'];

export const STATUSNAME: Readonly<Record<string, string>> = {
  aktiv: 'aktiv',
  wartung: 'in Wartung',
  ausser_betrieb: 'außer Betrieb',
  ausgemustert: 'ausgemustert',
};

/**
 * EIN TRAINER IST NICHT IN WARTUNG (Auftraggeber, 09.09.2026).
 *
 * `ressource.status` gilt für Flächen, Räume, Fahrzeuge **und Menschen**, und
 * zwei seiner vier Werte sind für einen Menschen falsch: „in Wartung" und
 * „außer Betrieb" sagt man über eine Gleitfläche, nicht über einen Trainer.
 * Sie stehen deshalb am Trainer nicht zur Wahl — und die Datenbank weist sie
 * dort auch ab (Migration 0164).
 *
 * Was bleibt: `aktiv` und `ausgemustert`. Wer vorübergehend nicht kann, ist
 * damit nicht „außer Betrieb", sondern hat eine **Sperrzeit mit Grund**
 * (RES-15) — die nennt Grund und Zeitraum, der Status nennt beides nicht.
 */
export function statusZurArt(art: string): readonly string[] {
  return art === 'trainer' ? ['aktiv', 'ausgemustert'] : STATUS;
}

/** Am Menschen heißt derselbe Wert anders als an der Fläche. */
export function statusname(status: string, art: string): string {
  if (art === 'trainer' && status === 'ausgemustert') return 'nicht mehr im Einsatz';
  return STATUSNAME[status] ?? status;
}

export type Ressourcenkonfiguration = {
  readonly id: string;
  readonly kennung: string;
  readonly bezeichnung: string;
  readonly art: Ressourcenart;
  readonly standort: string | null;
  readonly status: string;
  readonly belegungsart: 'ausschliesslich' | 'plaetze';
  readonly platzzahl: number | null;
  /**
   * ÄA-21: `intern`, `extern` — oder `null` bei allem, was keine Person ist.
   *
   * Der Auftraggeber am 11.09.2026: „es sind beides trainer, intern und
   * extern." Daran hängen zwei Regeln: die Auftragsverarbeitung (RES-39) und
   * die nachgelagerte Besetzung (RES-49).
   */
  readonly zugehoerigkeit: 'intern' | 'extern' | null;
  readonly auftragsverarbeitung: 'entfaellt' | 'fehlt' | 'liegt_vor' | 'gekuendigt';
  /** RES-39: Wozu er die Daten bekommt, im Wortlaut der Vereinbarung. */
  readonly zweckbindung: string | null;
  /** RES-39: Wann die Vereinbarung geschlossen wurde, als JJJJ-MM-TT. */
  readonly vereinbarungAm: string | null;
  readonly verantwortlicheRolle: string | null;
  readonly gehoertZu: string | null;
  readonly gehoertZuName: string | null;
  readonly bemerkung: string | null;
  readonly eigenschaften: readonly Eigenschaftszeile[];
  readonly sperrzeiten: readonly Sperrzeitzeile[];
  readonly rollen: readonly { readonly kennung: string; readonly bezeichnung: string }[];
  readonly standorte: readonly { readonly kennung: string; readonly bezeichnung: string }[];
  /**
   * RES-13: Wo diese Ressource außerdem eingesetzt werden darf.
   *
   * „Ressourcen können mehreren Standorten und Mandanten zugeordnet werden."
   * `standort` daneben bleibt der eine Ort, an dem sie verortet ist — daran
   * hängen Tagesbild (RES-32), Liste (RES-17) und Vorauswahl. Leer heißt: nur
   * der eigene Standort, und ohne den überall.
   */
  readonly einsatzorte: readonly string[];
  /**
   * Die Qualifikationen zur Auswahl (RES-54 i. d. F. ÄA-13).
   *
   * Eine gepflegte Liste und kein Freitext: Verglichen wird Zeichen für
   * Zeichen (TAG-14), und „PKW" am Produkt trifft „Pkw" am Trainer nie.
   */
  readonly qualifikationen: readonly {
    readonly schluessel: string;
    readonly bezeichnung: string;
    readonly aktiv: boolean;
  }[];
  readonly traeger: readonly { readonly id: string; readonly bezeichnung: string }[];
};

export async function konfiguration(ressource: string): Promise<Ressourcenkonfiguration> {
  return rufe(`/ressourcen/${ressource}/konfiguration`);
}

/**
 * Die Stammdaten ändern (RES-01, RES-03).
 *
 * Nur genannte Felder werden gesetzt. `null` heißt „weg damit", ein
 * fehlendes Feld heißt „dazu sage ich nichts" — sonst löschte das Speichern
 * eines Ausschnitts alles, was er nicht kennt.
 */
export async function aendern(
  ressource: string,
  felder: {
    kennung?: string;
    bezeichnung?: string;
    standort?: string | null;
    status?: string;
    belegungsart?: 'ausschliesslich' | 'plaetze';
    platzzahl?: number | null;
    verantwortlicheRolle?: string | null;
    gehoertZu?: string | null;
    bemerkung?: string | null;
    zugehoerigkeit?: 'intern' | 'extern';
    auftragsverarbeitung?: 'entfaellt' | 'fehlt' | 'liegt_vor' | 'gekuendigt';
    zweckbindung?: string | null;
    vereinbarungAm?: string | null;
    /** RES-13: die vollständige Liste der weiteren Einsatzorte — sie ersetzt. */
    einsatzorte?: readonly string[];
  },
): Promise<{ readonly geaendert: true }> {
  return rufe(`/ressourcen/${ressource}`, { verfahren: 'PATCH', rumpf: felder });
}

export async function eigenschaftAnlegen(
  ressource: string,
  eingabe: {
    art: Eigenschaftsart;
    schluessel: string;
    bezeichnung: string | null;
    gueltigAb: string | null;
    gueltigBis: string | null;
    nachweis: string | null;
  },
): Promise<{ readonly id: string }> {
  return rufe(`/ressourcen/${ressource}/eigenschaften`, { verfahren: 'POST', rumpf: eingabe });
}

export async function eigenschaftEntfernen(ressource: string, id: string): Promise<void> {
  await rufe(`/ressourcen/${ressource}/eigenschaften/${id}`, { verfahren: 'DELETE' });
}

export async function sperrzeitAnlegen(
  ressource: string,
  eingabe: {
    art: Sperrart;
    grund: string;
    von: string;
    bis: string;
    wiederholungTage: number | null;
  },
): Promise<{ readonly id: string }> {
  return rufe(`/ressourcen/${ressource}/sperrzeiten`, { verfahren: 'POST', rumpf: eingabe });
}

export async function sperrzeitEntfernen(ressource: string, id: string): Promise<void> {
  await rufe(`/ressourcen/${ressource}/sperrzeiten/${id}`, { verfahren: 'DELETE' });
}

// ---------------------------------------------------------------------------
// Die Konfiguration eines Moduls (AeA-05)
// ---------------------------------------------------------------------------

/**
 * AeA-05: EINE ZEILE JE BAUSTEIN — auch für die ohne Eintrag.
 *
 * Der Änderungsantrag nennt das Risiko: „Ein vergessener Eintrag macht ein
 * Modul für einen Baustein unsichtbar — die Planung meldet dann ‚kein Modul
 * verfügbar', wo eines steht." Eine Liste, die nur das Eingetragene zeigt,
 * verbirgt genau diese Lücke. Leere `eintraege` sind deshalb eine Aussage.
 */
export type Eignungszeile = {
  readonly baustein: string;
  readonly bezeichnung: string;
  readonly art: string;
  readonly eintraege: readonly {
    readonly id: string;
    /** NULL heißt: mit jeder Fahrzeugklasse. */
    readonly fahrzeugklasse: string | null;
    readonly gruppenGleichzeitig: number | null;
    /** AeA-06: ideal, geeignet oder moeglich. */
    readonly eignungsgrad: Eignungsgrad;
    readonly bemerkung: string | null;
  }[];
};

/** AeA-06: die drei Stufen, in der Reihenfolge, in der die Planung waehlt. */
export type Eignungsgrad = 'ideal' | 'geeignet' | 'moeglich';

export const EIGNUNGSGRADE: readonly Eignungsgrad[] = ['ideal', 'geeignet', 'moeglich'];

/** Was die Stufe bedeutet — der Satz und nicht nur das Wort. */
export const EIGNUNGSGRADNAME: Readonly<Record<Eignungsgrad, string>> = {
  ideal: 'ideal',
  geeignet: 'geeignet',
  moeglich: 'möglich',
};

export const EIGNUNGSGRADSINN: Readonly<Record<Eignungsgrad, string>> = {
  ideal: 'Dafür ist das Modul da — die Planung nimmt es zuerst.',
  geeignet: 'Geht hier ohne Einschränkung, ist aber nicht die erste Wahl.',
  moeglich: 'Geht zur Not — bei Belegung, Witterung oder Umplanung.',
};

export type Fahrzeugklassenzeile = {
  readonly schluessel: string;
  readonly bezeichnung: string;
  readonly obergruppe: string | null;
  /** Ob mit ihr gefahren wird. Eine Obergruppe ist es nicht (AeA-05). */
  readonly konkret: boolean;
};

export async function eignungen(ressource: string): Promise<{
  readonly bausteine: readonly Eignungszeile[];
  readonly fahrzeugklassen: readonly Fahrzeugklassenzeile[];
}> {
  return rufe(`/ressourcen/${ressource}/eignungen`);
}

export async function eignungAnlegen(
  ressource: string,
  eingabe: {
    readonly baustein: string;
    readonly fahrzeugklasse: string | null;
    readonly gruppenGleichzeitig: number | null;
    readonly eignungsgrad: Eignungsgrad;
    readonly bemerkung: string | null;
  },
): Promise<{ readonly id: string }> {
  return rufe(`/ressourcen/${ressource}/eignungen`, { verfahren: 'POST', rumpf: eingabe });
}

export async function eignungsgradSetzen(
  ressource: string,
  eignung: string,
  eignungsgrad: Eignungsgrad,
): Promise<{ readonly geaendert: true }> {
  return rufe(`/ressourcen/${ressource}/eignungen/${eignung}`, {
    verfahren: 'PATCH',
    rumpf: { eignungsgrad },
  });
}

export async function eignungEntfernen(ressource: string, eignung: string): Promise<void> {
  await rufe(`/ressourcen/${ressource}/eignungen/${eignung}`, { verfahren: 'DELETE' });
}

// ---------------------------------------------------------------------------
// Die nachgelagerte Besetzung (RES-49, RES-33, RES-50)
// ---------------------------------------------------------------------------

/**
 * Eine offene Besetzung.
 *
 * `stundenBisFrist` ist negativ, wenn die Frist vorbei ist. Die Maske liest
 * `ueberfaellig` daneben, statt das Vorzeichen zu deuten — beim nächsten
 * Umbau der Sortierung wäre genau das die Stelle, an der es kippt.
 */
export type Besetzungszeile = {
  readonly termin: string;
  readonly beginn: string;
  readonly artikel: string;
  readonly artikelname: string;
  readonly ort: string | null;
  readonly terminstatus: string;
  readonly bedarf: string;
  readonly bedarfsname: string;
  readonly verlangt: number;
  readonly gedeckt: number;
  readonly luecke: number;
  readonly offeneAnfragen: number;
  readonly fristAm: string;
  readonly stundenBisFrist: number;
  readonly ueberfaellig: boolean;
};

/** `null` heißt: passt. Sonst sagt der Befund, was nicht passt (TAG-14). */
export type Trainerbefund = null | 'fehlt' | 'abgelaufen' | 'laeuft_ab';

export type Kandidatenzeile = {
  readonly ressource: string;
  readonly kennung: string;
  readonly bezeichnung: string;
  readonly art: Ressourcenart;
  readonly standort: string | null;
  readonly schonGefragt: boolean;
  readonly letzterStatus: string | null;
  readonly letzteBelegung: string | null;
  readonly qualifikation: Trainerbefund;  /**
   * RES-64: Steht am Termintag die vertragliche Grundlage? `ja`,
   * `keine_vereinbarung`, `keine_auftragsverarbeitung`, `beides_fehlt` — oder
   * `nicht_extern`, wenn sich die Frage nicht stellt (Raum, Fahrzeug, interner
   * Trainer).
   *
   * Eine ANZEIGE und keine Sperre: Eine Anfrage ist keine Beauftragung.
   */
  readonly beauftragung: Beauftragungsbefund;
};

/**
 * Was zu diesem Bedarf schon gefragt und geantwortet wurde (RES-33, RES-35).
 *
 * Die Gegenliste zu den Kandidaten: Wer gerade angefragt ist, blockiert sich
 * selbst (RES-22) und steht deshalb NICHT unter ihnen. Ohne den Verlauf
 * ließe sich nicht sagen, auf wen gewartet wird.
 */
export type Verlaufszeile = {
  readonly belegung: string;
  readonly ressource: string;
  readonly kennung: string;
  readonly bezeichnung: string;
  readonly versuch: number | null;
  readonly status: string;
  readonly angefragtAm: string;
  readonly verfaelltAm: string | null;
  readonly beantwortetAm: string | null;
  readonly absagegrund: string | null;
};

/** ÄW-DP-29, RES-33: Versuche und Frist der automatischen Anfrage aus den Einstellungen. */
export type Anfrageregel = { readonly versuche: number; readonly fristStunden: number };

export async function anfrageregel(): Promise<Anfrageregel> {
  return rufe('/ressourcen/besetzung/anfrageregel');
}

export async function besetzungslage(): Promise<readonly Besetzungszeile[]> {
  const antwort = await rufe<{ readonly zeilen: readonly Besetzungszeile[] }>(
    '/ressourcen/besetzung',
  );
  return antwort.zeilen;
}

export async function besetzungskandidaten(
  termin: string,
  bedarf: string,
): Promise<{
  readonly kandidaten: readonly Kandidatenzeile[];
  readonly verlauf: readonly Verlaufszeile[];
}> {
  return rufe(`/ressourcen/besetzung/${termin}/${bedarf}/kandidaten`);
}

/**
 * Einen Einsatz anfragen (RES-33) — auf Wunsch mit dem Honorar (RES-43, RES-70).
 *
 * DAS HONORAR IST OPTIONAL, und das ist eine fachliche Aussage: Ein Raum hat
 * keine Leistungsart, ein interner Trainer bekommt kein Honorar, und die
 * Honorartabelle war am Tag dieser Erweiterung leer. Eine Pflichtangabe hätte
 * die laufende Besetzung aller 46 Trainer angehalten.
 *
 * Wer das Feld setzt, braucht `artikel/honorar_sehen`; wer vom Tabellensatz
 * abweicht, einen Grund (RES-70). Beides entscheidet der Server.
 */
export async function einsatzAnfragen(eingabe: {
  readonly termin: string;
  readonly bedarf: string;
  readonly ressource: string;
  readonly honorar?: {
    /**
     * Die Kategorie: ein Fahrtraining oder eine Stundenzeile (0188). Den TARIF
     * — Wochentag oder Wochenende — entscheidet der Kalender; er ist keine
     * Eingabe.
     */
    readonly leistungsart: string;
    readonly menge?: string;
    /** Der im Einzelfall abweichend vereinbarte Satz — als Text (RND-01). */
    readonly satz?: string;
    readonly uebersteuerungsgrund?: string;
    /** Die bestätigten Zuschläge mit ihrer wirklichen Menge (RES-71). */
    readonly zuschlaege?: readonly {
      readonly zuschlagsart: string;
      readonly menge: string;
    }[];
  };
}): Promise<{ readonly id: string }> {
  return rufe('/ressourcen/besetzung/anfragen', { verfahren: 'POST', rumpf: eingabe });
}

/**
 * Die Antwort des Angefragten (RES-33).
 *
 * Gibt den Stand zurück, den die Zuordnung danach hat — bei einer bereits
 * beantworteten Anfrage also den alten. Die Maske zeigt ihn an, statt zu
 * behaupten, die Antwort sei angekommen.
 */
export async function einsatzAntwort(
  belegung: string,
  zusage: boolean,
  grund?: string,
): Promise<{ readonly stand: string }> {
  return rufe(`/ressourcen/besetzung/${belegung}/antwort`, {
    verfahren: 'POST',
    rumpf: zusage ? { zusage } : { zusage, grund },
  });
}

/**
 * Den Onlinezugang zu einem Trainer oder Dienstleister einrichten (RES-28).
 *
 * ============================================================================
 * DAS ANFANGSKENNWORT KOMMT EINMAL — UND WIRD NICHT GESPEICHERT
 * ============================================================================
 *
 * Nirgends, auch nicht im Zwischenspeicher der Maske über einen Maskenwechsel
 * hinaus. Wer es verliert, setzt es zurück; das ist der richtige Weg und
 * billiger als ein Kennwort, das irgendwo lesbar liegt (LEG-13).
 *
 * Der persönliche Zugangslink, den RES-28 als ersten Weg nennt, folgt. Bis
 * dahin gilt der zweite Weg derselben Anforderung: eigenes Passwort mit
 * zweitem Faktor, nach den Regeln aus Kapitel 15 — einschließlich des
 * erzwungenen Wechsels beim ersten Anmelden (BEN-06).
 */
export async function zugangEinrichten(
  ressource: string,
  angabe: { readonly anmeldename: string; readonly email: string },
): Promise<{ readonly benutzer: string; readonly anfangskennwort: string }> {
  return rufe(`/ressourcen/${encodeURIComponent(ressource)}/zugang`, {
    verfahren: 'POST',
    rumpf: angabe,
  });
}

/** AeA-21: Wie die Zugehörigkeit in der Maske heißt. */
export const ZUGEHOERIGKEITSNAME: Record<string, string> = {
  intern: 'intern (angestellt)',
  extern: 'extern (im Auftrag)',
};

/**
 * RES-39: Wie der Vertragsstand in der Maske heißt.
 *
 * `fehlt` steht absichtlich nicht als „offen" da: Es ist kein Vorgang, der
 * läuft, sondern eine Lücke mit Folge — ohne Vereinbarung bleibt das Tagesbild
 * zu (ÄA-20).
 */
export const VERTRAGSSTANDNAME: Record<string, string> = {
  entfaellt: 'entfällt (nicht extern)',
  fehlt: 'keine Vereinbarung hinterlegt',
  liegt_vor: 'Vereinbarung liegt vor',
  gekuendigt: 'gekündigt',
};
